import React, { useState } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { Clock, ArrowLeft, Share2, Calculator, Check, Copy, Twitter, Linkedin } from 'lucide-react';
import SEO from '../../components/SEO';
import { Button } from '../../components/ui';
import { blogPosts } from './BlogPage';

// Blog post content - in production, this would come from a CMS
const blogContent = {
  'calculate-concrete-patio': {
    title: 'How to Calculate Concrete for a Patio or Slab',
    category: 'Concrete',
    readTime: '5 min read',
    date: '2026-01-05',
    content: `
      <p class="lead">Whether you're pouring a patio, driveway, or foundation, getting the right amount of concrete is crucial. Too little means delays and cold joints. Too much means wasted money. Here's how to calculate exactly what you need.</p>

      <h2>The Basic Formula</h2>
      <p>Concrete is measured in cubic yards in the US. The formula is simple:</p>
      <div class="formula">
        <strong>Cubic Yards = (Length × Width × Depth) ÷ 27</strong>
      </div>
      <p>All measurements should be in feet. We divide by 27 because there are 27 cubic feet in a cubic yard.</p>

      <h2>Step-by-Step Calculation</h2>
      <p>Let's calculate concrete for a 10' × 12' patio that's 4 inches thick:</p>
      <ol>
        <li><strong>Convert depth to feet:</strong> 4 inches ÷ 12 = 0.33 feet</li>
        <li><strong>Calculate volume:</strong> 10 × 12 × 0.33 = 39.6 cubic feet</li>
        <li><strong>Convert to yards:</strong> 39.6 ÷ 27 = 1.47 cubic yards</li>
      </ol>

      <h2>Don't Forget the Waste Factor!</h2>
      <p>Always add 10% for waste. Concrete can spill, forms may bulge, and the ground is rarely perfectly level.</p>
      <div class="callout">
        <strong>1.47 × 1.10 = 1.62 cubic yards</strong>
        <p>Round up to 1.75 yards when ordering</p>
      </div>

      <h2>Common Patio Sizes</h2>
      <table>
        <thead>
          <tr>
            <th>Size (feet)</th>
            <th>Depth (inches)</th>
            <th>Cubic Yards</th>
            <th>With 10% Waste</th>
          </tr>
        </thead>
        <tbody>
          <tr><td>10 × 10</td><td>4"</td><td>1.23</td><td>1.35</td></tr>
          <tr><td>10 × 12</td><td>4"</td><td>1.48</td><td>1.63</td></tr>
          <tr><td>12 × 14</td><td>4"</td><td>2.07</td><td>2.28</td></tr>
          <tr><td>15 × 20</td><td>4"</td><td>3.70</td><td>4.07</td></tr>
          <tr><td>20 × 24</td><td>4"</td><td>5.93</td><td>6.52</td></tr>
        </tbody>
      </table>

      <h2>Bag vs. Ready-Mix</h2>
      <p><strong>Bags (80 lb):</strong> Best for projects under 1/3 yard. Each bag makes about 0.6 cubic feet.</p>
      <p><strong>Ready-Mix Truck:</strong> More economical for anything over 1 yard. Most companies have a 1-yard minimum.</p>

      <h2>Cost Estimates</h2>
      <p>Prices vary by location, but here are typical ranges:</p>
      <ul>
        <li><strong>Ready-mix concrete:</strong> $120-$150 per cubic yard</li>
        <li><strong>80 lb bags:</strong> $5-7 each (you need ~45 bags per yard)</li>
        <li><strong>Short load fee:</strong> $50-100 for orders under 3 yards</li>
      </ul>

      <h2>Pro Tips</h2>
      <ul>
        <li>Order slightly more than you need - running short mid-pour creates weak spots</li>
        <li>Consider fiber mesh or rebar for patios over 100 sq ft</li>
        <li>Form height should be exactly 4" for a 4" slab</li>
        <li>Have help ready - concrete waits for no one!</li>
      </ul>
    `,
    relatedPosts: ['drywall-material-calculator', 'construction-waste-factors'],
  },
  'drywall-material-calculator': {
    title: 'Drywall Installation: Complete Material Calculator Guide',
    category: 'Drywall',
    readTime: '6 min read',
    date: '2026-01-03',
    content: `
      <p class="lead">Calculating drywall materials goes beyond just counting sheets. You need mud, tape, screws, and corner bead too. Here's your complete guide to getting it right.</p>

      <h2>Calculating Drywall Sheets</h2>
      <p>Standard drywall comes in 4×8 (32 sq ft), 4×10 (40 sq ft), and 4×12 (48 sq ft) sheets.</p>
      
      <div class="formula">
        <strong>Sheets Needed = Total Square Footage ÷ Sheet Size × 1.10</strong>
      </div>

      <h3>Room Example: 12' × 14' with 8' Ceilings</h3>
      <ol>
        <li><strong>Wall area:</strong> (12 + 14 + 12 + 14) × 8 = 416 sq ft</li>
        <li><strong>Ceiling area:</strong> 12 × 14 = 168 sq ft</li>
        <li><strong>Total:</strong> 584 sq ft</li>
        <li><strong>Subtract openings:</strong> 2 windows (30 sq ft) + 2 doors (40 sq ft) = 70 sq ft</li>
        <li><strong>Net area:</strong> 514 sq ft</li>
        <li><strong>Sheets (4×8):</strong> 514 ÷ 32 × 1.10 = 17.7 → <strong>18 sheets</strong></li>
      </ol>

      <h2>Joint Compound (Mud)</h2>
      <p>Plan for multiple coats. Here's what you need:</p>
      <ul>
        <li><strong>Pre-mixed all-purpose:</strong> 1 gallon per 100 sq ft of drywall</li>
        <li><strong>For 514 sq ft:</strong> ~5-6 gallons</li>
        <li>Buy one 5-gallon bucket for most rooms</li>
      </ul>

      <h2>Drywall Tape</h2>
      <ul>
        <li><strong>Paper tape:</strong> 1 roll (500 ft) per 500 sq ft</li>
        <li><strong>Mesh tape:</strong> Similar coverage, easier for beginners</li>
        <li>For our example room: 1-2 rolls</li>
      </ul>

      <h2>Screws</h2>
      <p>Use 1-1/4" coarse thread drywall screws:</p>
      <ul>
        <li><strong>Per sheet:</strong> 28-32 screws</li>
        <li><strong>Per 1000 sq ft:</strong> ~1,000 screws</li>
        <li><strong>Spacing:</strong> Every 12" on edges, 16" in field</li>
      </ul>

      <h2>Corner Bead</h2>
      <p>Measure all outside corners:</p>
      <ul>
        <li>Metal corner bead comes in 8' and 10' lengths</li>
        <li>Paper-faced corner bead for faster finishing</li>
        <li>Count corners and buy accordingly</li>
      </ul>

      <h2>Complete Shopping List</h2>
      <p>For a 12' × 14' room with 8' ceilings:</p>
      <table>
        <thead>
          <tr><th>Material</th><th>Quantity</th><th>Est. Cost</th></tr>
        </thead>
        <tbody>
          <tr><td>Drywall sheets (4×8)</td><td>18</td><td>$180-$270</td></tr>
          <tr><td>Joint compound</td><td>5 gallons</td><td>$25-$35</td></tr>
          <tr><td>Paper tape</td><td>2 rolls</td><td>$8-$12</td></tr>
          <tr><td>Drywall screws</td><td>1 lb box</td><td>$8-$12</td></tr>
          <tr><td>Corner bead</td><td>As needed</td><td>$2-4 each</td></tr>
          <tr><td><strong>Total</strong></td><td></td><td><strong>$220-$330</strong></td></tr>
        </tbody>
      </table>
    `,
    relatedPosts: ['paint-calculator-guide', 'construction-waste-factors'],
  },
  'paint-calculator-guide': {
    title: 'Paint Calculator: How Much Paint Do You Really Need?',
    category: 'Paint',
    readTime: '4 min read',
    date: '2025-12-28',
    content: `
      <p class="lead">Over-buying paint is incredibly common. With paint costing $30-$60 per gallon, getting the right amount saves real money. Here's how to calculate it accurately.</p>

      <h2>The Basic Formula</h2>
      <div class="formula">
        <strong>Gallons = Square Footage ÷ 350 × Number of Coats</strong>
      </div>
      <p>One gallon covers approximately 350-400 square feet per coat. Use 350 for a conservative estimate.</p>

      <h2>Calculating Wall Area</h2>
      <ol>
        <li><strong>Perimeter:</strong> Add up all wall lengths</li>
        <li><strong>Multiply by height:</strong> Perimeter × ceiling height</li>
        <li><strong>Subtract openings:</strong> Windows (~15 sq ft each), doors (~20 sq ft each)</li>
      </ol>

      <h3>Example: 12' × 12' Bedroom</h3>
      <ol>
        <li>Perimeter: 12 + 12 + 12 + 12 = 48 feet</li>
        <li>Wall area: 48 × 8 = 384 sq ft</li>
        <li>Subtract: 2 windows (30) + 1 door (20) = 50 sq ft</li>
        <li>Paintable area: 334 sq ft</li>
        <li>Two coats: 334 ÷ 350 × 2 = 1.9 gallons</li>
      </ol>

      <h2>When You Need Two Coats</h2>
      <ul>
        <li>Painting over a darker color</li>
        <li>Using a lighter color</li>
        <li>New drywall (use primer + 2 coats)</li>
        <li>Changing color families (blue to red, etc.)</li>
      </ul>

      <h2>When One Coat Works</h2>
      <ul>
        <li>Same color touch-ups</li>
        <li>Using a premium paint with primer included</li>
        <li>Going from light to similar light color</li>
      </ul>

      <h2>Don't Forget Trim!</h2>
      <p>Trim paint covers about the same, but you use less:</p>
      <ul>
        <li>Baseboards: 1 quart per 100 linear feet</li>
        <li>Door frames: 1 quart per 2-3 doors</li>
        <li>Crown molding: 1 quart per 75-100 linear feet</li>
      </ul>

      <h2>Quick Reference Chart</h2>
      <table>
        <thead>
          <tr><th>Room Size</th><th>Wall Area</th><th>1 Coat</th><th>2 Coats</th></tr>
        </thead>
        <tbody>
          <tr><td>10×10</td><td>280 sq ft</td><td>1 gal</td><td>2 gal</td></tr>
          <tr><td>12×12</td><td>334 sq ft</td><td>1 gal</td><td>2 gal</td></tr>
          <tr><td>12×14</td><td>376 sq ft</td><td>1.5 gal</td><td>2.5 gal</td></tr>
          <tr><td>15×15</td><td>440 sq ft</td><td>1.5 gal</td><td>3 gal</td></tr>
        </tbody>
      </table>

      <h2>Pro Tips</h2>
      <ul>
        <li>Buy an extra quart for touch-ups later</li>
        <li>Keep paint can info for exact color matching</li>
        <li>Test color on the wall before buying full gallons</li>
        <li>Higher quality paint often means better coverage with fewer coats</li>
      </ul>
    `,
    relatedPosts: ['drywall-material-calculator', 'construction-estimating-mistakes'],
  },
  'construction-waste-factors': {
    title: 'Construction Waste Factors: Why You Need 10-15% Extra Materials',
    category: 'Tips',
    readTime: '5 min read',
    date: '2025-12-20',
    content: `
      <p class="lead">Every professional contractor orders extra materials. Not because they're bad at math—because real-world construction is messy. Here's why waste factors matter and how much to add for each material.</p>

      <h2>What Is a Waste Factor?</h2>
      <p>A waste factor is the extra percentage of materials you order to account for:</p>
      <ul>
        <li><strong>Cutting:</strong> Pieces that don't fit and create scraps</li>
        <li><strong>Damage:</strong> Materials damaged during transport or installation</li>
        <li><strong>Mistakes:</strong> Cuts in wrong places, measurements errors</li>
        <li><strong>Overages:</strong> Ground not level, walls not square</li>
      </ul>

      <h2>Waste Factor by Material</h2>
      <table>
        <thead>
          <tr><th>Material</th><th>Standard Waste</th><th>Complex Install</th></tr>
        </thead>
        <tbody>
          <tr><td>Concrete</td><td>10%</td><td>15%</td></tr>
          <tr><td>Drywall</td><td>10%</td><td>15%</td></tr>
          <tr><td>Paint</td><td>10%</td><td>15%</td></tr>
          <tr><td>Flooring (straight)</td><td>10%</td><td>15%</td></tr>
          <tr><td>Flooring (diagonal)</td><td>15%</td><td>20%</td></tr>
          <tr><td>Tile</td><td>10%</td><td>15%+</td></tr>
          <tr><td>Lumber</td><td>10%</td><td>15%</td></tr>
          <tr><td>Roofing</td><td>10%</td><td>15%</td></tr>
          <tr><td>Siding</td><td>10%</td><td>15%</td></tr>
        </tbody>
      </table>

      <h2>When to Use Higher Waste Factors</h2>
      <ul>
        <li><strong>Diagonal installations:</strong> Add 15-20%</li>
        <li><strong>Complex patterns:</strong> Add 15-20%</li>
        <li><strong>Older homes:</strong> Add 15% (walls rarely square)</li>
        <li><strong>First-time DIYers:</strong> Add 15%</li>
        <li><strong>Matching existing:</strong> Add 20% (color matching is tricky)</li>
      </ul>

      <h2>The Cost of Running Short</h2>
      <p>Running short costs more than the materials:</p>
      <ul>
        <li><strong>Project delays:</strong> Waiting for new materials</li>
        <li><strong>Dye lot differences:</strong> New batches may not match</li>
        <li><strong>Delivery fees:</strong> Another trip or shipping charge</li>
        <li><strong>Cold joints:</strong> In concrete, can cause structural issues</li>
      </ul>

      <h2>Calculating with Waste Factor</h2>
      <div class="formula">
        <strong>Total Needed = Calculated Amount × (1 + Waste %)</strong>
      </div>
      <p>Example: 100 sq ft of tile with 15% waste = 100 × 1.15 = 115 sq ft</p>

      <h2>What to Do with Leftovers</h2>
      <ul>
        <li><strong>Keep extras:</strong> Store for repairs and touch-ups</li>
        <li><strong>Return policy:</strong> Many stores accept returns within 30-90 days</li>
        <li><strong>Sell online:</strong> Marketplace, Craigslist for large amounts</li>
        <li><strong>Donate:</strong> Habitat for Humanity ReStore accepts materials</li>
      </ul>
    `,
    relatedPosts: ['construction-estimating-mistakes', 'calculate-concrete-patio'],
  },
  'construction-estimating-mistakes': {
    title: '5 Common Construction Estimating Mistakes (And How to Avoid Them)',
    category: 'Tips',
    readTime: '7 min read',
    date: '2025-12-15',
    content: `
      <p class="lead">Even experienced builders make estimating mistakes. These five errors cost time and money—learn to avoid them on your next project.</p>

      <h2>Mistake #1: Forgetting the Waste Factor</h2>
      <p><strong>The problem:</strong> Calculating exact amounts without accounting for cuts, damage, and errors.</p>
      <p><strong>The fix:</strong> Always add 10-15% to your calculated materials. For complex installations like diagonal flooring, add 15-20%.</p>
      <div class="callout">
        <strong>Real example:</strong> A 500 sq ft floor needs 550-575 sq ft of flooring, not 500.
      </div>

      <h2>Mistake #2: Measuring Wrong</h2>
      <p><strong>The problem:</strong> Measuring once, or measuring in the wrong units.</p>
      <p><strong>The fix:</strong></p>
      <ul>
        <li>Measure twice, order once</li>
        <li>Convert all measurements to the same unit</li>
        <li>Draw a diagram with all dimensions</li>
        <li>Double-check your math</li>
      </ul>

      <h2>Mistake #3: Forgetting Hidden Areas</h2>
      <p><strong>The problem:</strong> Missing closets, alcoves, or areas behind doors.</p>
      <p><strong>What people forget:</strong></p>
      <ul>
        <li>Closet interiors</li>
        <li>Behind open doors</li>
        <li>Alcoves and nooks</li>
        <li>Under-stair areas</li>
        <li>Ceiling areas (paint, drywall)</li>
      </ul>

      <h2>Mistake #4: Not Accounting for Layout</h2>
      <p><strong>The problem:</strong> Square footage doesn't account for how materials actually install.</p>
      <p><strong>Examples:</strong></p>
      <ul>
        <li><strong>Drywall:</strong> An 8' ceiling means 8' sheets work perfectly; 9' ceilings need more cuts</li>
        <li><strong>Flooring:</strong> Narrow rooms waste more material per linear foot</li>
        <li><strong>Roofing:</strong> More complex roof = more waste at valleys and ridges</li>
      </ul>

      <h2>Mistake #5: Ignoring Labor Time</h2>
      <p><strong>The problem:</strong> Materials are only part of the cost. Labor is often 40-60% of total project cost.</p>
      <p><strong>Hidden time costs:</strong></p>
      <ul>
        <li>Prep work (moving furniture, taping, covering)</li>
        <li>Cleanup and debris removal</li>
        <li>Trips to the hardware store</li>
        <li>Drying time between coats</li>
        <li>Inspection delays</li>
      </ul>

      <h2>How CalcBuildAI Helps</h2>
      <p>Our AI calculator automatically:</p>
      <ul>
        <li>Adds appropriate waste factors for each material type</li>
        <li>Considers installation patterns (diagonal, straight, etc.)</li>
        <li>Includes all necessary supplementary materials</li>
        <li>Provides cost ranges based on current prices</li>
      </ul>

      <h2>Your Estimating Checklist</h2>
      <ol>
        <li>✓ Measure all areas twice</li>
        <li>✓ Draw a diagram with dimensions</li>
        <li>✓ Include hidden areas (closets, alcoves)</li>
        <li>✓ Add waste factor (10-15% minimum)</li>
        <li>✓ Consider installation pattern</li>
        <li>✓ List ALL materials (not just the main one)</li>
        <li>✓ Factor in labor time</li>
        <li>✓ Round up, not down</li>
      </ol>
    `,
    relatedPosts: ['construction-waste-factors', 'calculate-concrete-patio'],
  },
};

const BlogPostPage = () => {
  const { slug } = useParams();
  const post = blogContent[slug];
  const postMeta = blogPosts.find(p => p.slug === slug);
  const [copied, setCopied] = useState(false);

  if (!post || !postMeta) {
    return <Navigate to="/blog" replace />;
  }

  const relatedPosts = post.relatedPosts
    ?.map(relatedSlug => blogPosts.find(p => p.slug === relatedSlug))
    .filter(Boolean) || [];

  // Share functions
  const shareUrl = typeof window !== 'undefined' ? window.location.href : `https://calcbuildai.com/blog/${slug}`;
  const shareTitle = post.title;

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: shareTitle,
          text: postMeta.excerpt,
          url: shareUrl,
        });
      } catch (err) {
        if (err.name !== 'AbortError') {
          console.error('Share failed:', err);
        }
      }
    }
  };

  const handleTwitterShare = () => {
    const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareTitle)}&url=${encodeURIComponent(shareUrl)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleLinkedInShare = () => {
    const url = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <>
      <SEO
        title={post.title}
        description={postMeta.excerpt}
        canonical={`/blog/${slug}`}
        type="article"
      />

      <div className="min-h-screen bg-white">
        {/* Header */}
        <section className="bg-gradient-to-b from-blue-50 to-white py-12">
          <div className="max-w-3xl mx-auto px-4">
            {/* Breadcrumb */}
            <nav className="mb-6 text-sm">
              <ol className="flex items-center gap-2">
                <li><Link to="/" className="text-gray-500 hover:text-blue-600">Home</Link></li>
                <li className="text-gray-400">/</li>
                <li><Link to="/blog" className="text-gray-500 hover:text-blue-600">Blog</Link></li>
                <li className="text-gray-400">/</li>
                <li className="text-gray-900 font-medium truncate max-w-[200px]">{post.title}</li>
              </ol>
            </nav>

            <Link 
              to="/blog" 
              className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 mb-6"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Blog
            </Link>

            <div className="flex items-center gap-3 mb-4">
              <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium">
                {post.category}
              </span>
              <span className="text-gray-500 flex items-center gap-1 text-sm">
                <Clock className="w-4 h-4" />
                {post.readTime}
              </span>
              <span className="text-gray-400">•</span>
              <span className="text-gray-500 text-sm">
                {new Date(post.date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
              </span>
            </div>

            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              {post.title}
            </h1>
            
            {/* Featured Image */}
            {postMeta.image && (
              <div className="rounded-2xl overflow-hidden shadow-lg">
                <img 
                  src={postMeta.image}
                  alt={postMeta.imageAlt || post.title}
                  className="w-full h-64 md:h-80 object-cover"
                />
              </div>
            )}
          </div>
        </section>

        {/* Content */}
        <section className="py-8">
          <div className="max-w-3xl mx-auto px-4">
            <article 
              className="prose prose-lg max-w-none prose-headings:text-gray-900 prose-p:text-gray-700 prose-li:text-gray-700 prose-strong:text-gray-900"
              dangerouslySetInnerHTML={{ __html: post.content }}
            />

            {/* CTA Box */}
            <div className="my-12 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-8 border border-blue-100">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-blue-600 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Calculator className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">
                    Calculate Your Materials Instantly
                  </h3>
                  <p className="text-gray-600 mb-4">
                    Skip the manual calculations. Describe your project and get accurate estimates in seconds.
                  </p>
                  <Link to="/">
                    <Button icon={Calculator}>
                      Try Free Calculator
                    </Button>
                  </Link>
                </div>
              </div>
            </div>

            {/* Share */}
            <div className="flex flex-wrap items-center gap-3 py-6 border-t border-b">
              <span className="font-medium text-gray-900">Share this article:</span>
              
              {/* Native Share (mobile) */}
              {typeof navigator !== 'undefined' && navigator.share && (
                <button 
                  onClick={handleNativeShare}
                  className="p-2.5 hover:bg-blue-50 rounded-lg transition-colors group"
                  title="Share"
                >
                  <Share2 className="w-5 h-5 text-gray-600 group-hover:text-blue-600" />
                </button>
              )}
              
              {/* Copy Link */}
              <button 
                onClick={handleCopyLink}
                className="p-2.5 hover:bg-blue-50 rounded-lg transition-colors group flex items-center gap-1.5"
                title="Copy link"
              >
                {copied ? (
                  <>
                    <Check className="w-5 h-5 text-green-600" />
                    <span className="text-sm text-green-600">Copied!</span>
                  </>
                ) : (
                  <Copy className="w-5 h-5 text-gray-600 group-hover:text-blue-600" />
                )}
              </button>
              
              {/* Twitter */}
              <button 
                onClick={handleTwitterShare}
                className="p-2.5 hover:bg-blue-50 rounded-lg transition-colors group"
                title="Share on Twitter"
              >
                <Twitter className="w-5 h-5 text-gray-600 group-hover:text-blue-400" />
              </button>
              
              {/* LinkedIn */}
              <button 
                onClick={handleLinkedInShare}
                className="p-2.5 hover:bg-blue-50 rounded-lg transition-colors group"
                title="Share on LinkedIn"
              >
                <Linkedin className="w-5 h-5 text-gray-600 group-hover:text-blue-700" />
              </button>
            </div>
          </div>
        </section>

        {/* Related Posts */}
        {relatedPosts.length > 0 && (
          <section className="py-12 bg-gray-50">
            <div className="max-w-3xl mx-auto px-4">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Related Articles</h2>
              <div className="grid md:grid-cols-2 gap-4">
                {relatedPosts.map((related) => (
                  <Link
                    key={related.slug}
                    to={`/blog/${related.slug}`}
                    className="bg-white p-4 rounded-xl border hover:border-blue-300 hover:shadow-sm transition-all"
                  >
                    <span className="text-xs font-medium text-blue-600">{related.category}</span>
                    <h3 className="font-semibold text-gray-900 mt-1 hover:text-blue-600">
                      {related.title}
                    </h3>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </div>

      {/* Inline styles for blog content */}
      <style>{`
        .prose .lead {
          font-size: 1.25rem;
          color: #4b5563;
          line-height: 1.7;
          margin-bottom: 2rem;
        }
        .prose .formula {
          background: #f3f4f6;
          border-left: 4px solid #3b82f6;
          padding: 1rem 1.5rem;
          margin: 1.5rem 0;
          border-radius: 0 0.5rem 0.5rem 0;
          font-family: monospace;
        }
        .prose .callout {
          background: #eff6ff;
          border: 1px solid #bfdbfe;
          padding: 1rem 1.5rem;
          margin: 1.5rem 0;
          border-radius: 0.75rem;
        }
        .prose .callout p {
          margin: 0.5rem 0 0;
          color: #1e40af;
        }
        .prose table {
          width: 100%;
          border-collapse: collapse;
          margin: 1.5rem 0;
        }
        .prose th, .prose td {
          border: 1px solid #e5e7eb;
          padding: 0.75rem 1rem;
          text-align: left;
        }
        .prose th {
          background: #f9fafb;
          font-weight: 600;
        }
        .prose tr:hover {
          background: #f9fafb;
        }
      `}</style>
    </>
  );
};

export default BlogPostPage;
